// api/product-analytics.ts
import { apiClient } from "./api";
import { queryOptions } from "@tanstack/react-query";

// Interfaces
export interface DailyUsage {
  date: string;
  credits: number;
  calls: number;
}

export interface ProductMetrics {
  total_credits: number;
  total_calls: number;
  unique_agents: number;
  average_credits_per_call: number;
  growth_rate: number;
  peak_usage: {
    credits: number;
    timestamp: string;
  };
  summary: {
    total_credits?: number;
    total_calls?: number;
    average_credits_per_call?: number;
    growth_rate?: number;
  };
}

export interface ProductAnalytics {
  product_id: string;
  summary: ProductMetrics;
  daily_usage: DailyUsage[];
  usage_periods: {
    name: string;
    credits: number;
    calls: number;
    growth: number;
  }[];
  period: {
    start: string;
    end: string;
  };
}

export interface ProductRanking {
  product_id: string;
  value: number;
  metric: string;
}

export interface ProductInsights {
  rankings: {
    by_credit_usage: ProductRanking[];
    by_efficiency: ProductRanking[];
    by_growth: ProductRanking[];
  };
  trends: {
    fastest_growing: string;
    most_efficient: string;
    highest_usage: string;
  };
  period: {
    start: string;
    end: string;
  };
}

export interface ProductSummary {
  products: Record<string, ProductMetrics & { daily_usage: DailyUsage[] }>;
  summary: {
    total_products: number;
    total_credits_used: number;
    total_api_calls: number;
    avg_credits_per_product: number;
    period: {
      start: string;
      end: string;
    };
  };
}

// API Functions
export const getProductsSummary = async (
  startDate?: string,
  endDate?: string,
  productIds?: string[],
): Promise<ProductSummary> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (productIds)
      productIds.forEach((id) => params.append("product_ids", id));

    const response = await apiClient.get(
      `/api/products/analytics/summary?${params}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching products summary:", error);
    throw error;
  }
};

export const getProductInsights = async (
  startDate?: string,
  endDate?: string,
): Promise<ProductInsights> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const response = await apiClient.get(
      `/api/products/analytics/insights?${params}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching product insights:", error);
    throw error;
  }
};

export const getProductAnalytics = async (
  productId: string,
  startDate?: string,
  endDate?: string,
): Promise<ProductAnalytics> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const response = await apiClient.get(
      `/api/products/analytics/${productId}?${params}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching product analytics:", error);
    throw error;
  }
};

export const compareProducts = async (
  productIds: string[],
  startDate?: string,
  endDate?: string,
  metrics: string[] = ["credits", "calls", "efficiency", "growth"],
): Promise<Record<string, ProductMetrics>> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    productIds.forEach((id) => params.append("product_ids", id));
    metrics.forEach((metric) => params.append("metrics", metric));

    const response = await apiClient.get(
      `/api/products/analytics/comparison?${params}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error comparing products:", error);
    throw error;
  }
};

// React Query Options
export const productAnalyticsQueries = {
  all: () => queryOptions({ queryKey: ["product-analytics"] }),
  summary: (startDate?: string, endDate?: string, productIds?: string[]) =>
    queryOptions({
      queryKey: [
        "product-analytics",
        "summary",
        startDate,
        endDate,
        productIds,
      ],
      queryFn: () => getProductsSummary(startDate, endDate, productIds),
    }),

  insights: (startDate?: string, endDate?: string) =>
    queryOptions({
      queryKey: ["product-analytics", "insights", startDate, endDate],
      queryFn: () => getProductInsights(startDate, endDate),
    }),

  product: (productId: string, startDate?: string, endDate?: string) =>
    queryOptions({
      queryKey: ["product-analytics", productId, startDate, endDate],
      queryFn: () => getProductAnalytics(productId, startDate, endDate),
    }),

  comparison: (
    productIds: string[],
    startDate?: string,
    endDate?: string,
    metrics?: string[],
  ) =>
    queryOptions({
      queryKey: [
        "product-analytics",
        "comparison",
        productIds,
        startDate,
        endDate,
        metrics,
      ],
      queryFn: () => compareProducts(productIds, startDate, endDate, metrics),
    }),
};
